import Grid from '@mui/material/Grid';
import { FieldErrors, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  InputText,
  InputType,
  SectionTitle,
  SvgSingleNeutralBold,
} from '@insights-ltd/design-library/components';
import {
  LearnerRequest,
  LearnerWithEvaluation,
  RequestError,
  UpdateLearnerResponse,
  useUpdateLearner,
} from 'api';
import Button from '@mui/material/Button';
import { AlertTitle, Box, Snackbar, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { VALID_EMAIL_REGEX } from 'variables';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import CustomerExperienceLink from 'components/CustomerExperienceLink';
import { useAuth } from 'contexts/AuthContext';
import { isAdvancedUser, isGroupManager, isSuperAdmin } from 'utils/role';
import { useBetaEnabled } from 'features';
import { Role } from 'types/types';
import MergeLearnersDialog from './MergeLearnersDialog';
import MergeLearnersErrorDialog, {
  MERGE_ERROR_CONTENT_KEY,
} from './MergeLearnersErrorDialog';
import useMergeLearnersDialog from './useMergeLearnersDialog';

type EditLearnerFormProps = {
  learner: LearnerWithEvaluation;
  organisationId: string;
};
const EMAIL_UPDATE_FAILED = 'UPDATE_FAILED';
const EMAIL_ADDRESS_ALREADY_TAKEN = 'EMAIL_ADDRESS_ALREADY_TAKEN';
const EMAIL_UPDATE_NOT_PERMITTED = 'EMAIL_UPDATE_NOT_PERMITTED';
const EMAIL_ALREADY_EXISTS_IN_ORGANISATION =
  'EMAIL_ALREADY_EXISTS_IN_ORGANISATION';

const hasErrorCode = (error: unknown, errorCode: string) => {
  return error instanceof RequestError && error.errorCodes.includes(errorCode);
};

const canCombineLearners = (role?: Role) =>
  isSuperAdmin(role) || isGroupManager(role) || isAdvancedUser(role);

const emailErrorKey = (
  error: unknown,
  errors: FieldErrors<UpdateLearnerResponse>,
  isBetaEnabled: boolean,
  role?: Role,
): string | undefined => {
  const isClientServices = isSuperAdmin(role);

  const CONFLICT_ERROR =
    'ui.event-management.merge-learner.email-conflict.error';

  const fieldErrorType = errors.emailAddress?.type;

  if (fieldErrorType === 'required') {
    return 'ui.event-management.learner.edit.error.email-address-required';
  }
  if (fieldErrorType === 'pattern') {
    return 'ui.event-management.learner.edit.error.valid-email-address-required';
  }
  if (hasErrorCode(error, EMAIL_UPDATE_FAILED)) {
    return CONFLICT_ERROR;
  }

  if (!isBetaEnabled) return undefined;

  if (hasErrorCode(error, EMAIL_UPDATE_NOT_PERMITTED)) {
    return isClientServices
      ? 'ui.event-management.merge-learner.general-error'
      : CONFLICT_ERROR;
  }
  if (hasErrorCode(error, EMAIL_ADDRESS_ALREADY_TAKEN)) {
    return canCombineLearners(role)
      ? 'ui.event-management.merge-learner.email-exists.error'
      : CONFLICT_ERROR;
  }

  return undefined;
};

const StyledForm = styled('form')(({ theme }) => ({
  '& > * + *': {
    marginTop: theme.spacing(spacingSizeMap.L),
  },
}));

const EditLearnerForm = ({ learner, organisationId }: EditLearnerFormProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    mutate,
    isPending,
    isError,
    error,
    reset: resetMutation,
  } = useUpdateLearner(organisationId, learner.id, queryClient);

  const isBetaEnabled = useBetaEnabled('learner-edit-email-validation');

  const {
    offerMerge,
    reset: resetMergeFlow,
    isBusy: isMergeFlowBusy,
    lookupError: mergeLookupError,
    dialogProps: mergeDialogProps,
    errorDialogProps: mergeErrorDialogProps,
  } = useMergeLearnersDialog(organisationId, learner.id);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<UpdateLearnerResponse>({
    defaultValues: {
      ...learner,
      knownAs: learner.knownAs || '',
      emailAddress: learner.primaryEmail,
    },
  });

  const shouldOfferMerge = (err: unknown) =>
    isBetaEnabled &&
    canCombineLearners(user?.role) &&
    hasErrorCode(err, EMAIL_ALREADY_EXISTS_IN_ORGANISATION);

  const onSubmit = (formData: LearnerRequest) => {
    resetMergeFlow();
    mutate(
      { organisationId, learnerId: learner.id, formData },
      {
        onSuccess: () => navigate(`/learners/${organisationId}/${learner.id}`),
        onError: (err) => {
          if (shouldOfferMerge(err)) {
            offerMerge(formData.emailAddress);
          }
        },
      },
    );
  };

  const getEmailErrorMessageKey = () => {
    const mergeLookupErrorKey = mergeLookupError
      ? MERGE_ERROR_CONTENT_KEY
      : undefined;
    const errorKey =
      emailErrorKey(error, errors, isBetaEnabled, user?.role) ??
      mergeLookupErrorKey;

    if (!errorKey) {
      return undefined;
    }

    const customerExperienceLink = (
      <CustomerExperienceLink id="link-customer-exp" />
    );
    return (
      <Trans
        i18nKey={errorKey}
        values={
          errorKey === MERGE_ERROR_CONTENT_KEY
            ? { contactTeamName: t('ui.common.customer-experience-team') }
            : undefined
        }
        components={{
          LinkElement: customerExperienceLink,
          email: customerExperienceLink,
        }}
      />
    );
  };
  const emailErrorMessageKey = getEmailErrorMessageKey();

  const isGeneralError =
    isError && !emailErrorMessageKey && !shouldOfferMerge(error);

  return (
    <>
      <Box
        sx={{
          my: spacingSizeMap.L,
        }}
      >
        <SectionTitle
          icon={{ icon: <SvgSingleNeutralBold />, iconColour: 'pink' }}
          text={t('ui.event-management.learner.edit.learner-details')}
        />
      </Box>
      <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
        <InputText
          id="fullName"
          label={t('ui.event-management.learner.edit.full-name')}
          placeholder={t('ui.event-management.learner.edit.full-name')}
          helperText={
            errors.fullName &&
            t('ui.event-management.learner.edit.error.full-name-required')
          }
          error={Boolean(errors.fullName)}
          required
          fullWidth
          {...register('fullName', { required: true })}
        />
        <InputText
          id="knownAs"
          label={t('ui.event-management.learner.edit.known-as')}
          placeholder={t('ui.event-management.learner.edit.known-as')}
          helperText={
            errors.knownAs &&
            t('ui.event-management.learner.edit.error.known-as-required')
          }
          error={Boolean(errors.knownAs)}
          required
          fullWidth
          {...register('knownAs', { required: true })}
        />
        <InputText
          id="emailAddress"
          label={t('ui.event-management.learner.edit.email-address')}
          placeholder={t('ui.event-management.learner.edit.email-address')}
          type={InputType.EMAIL}
          helperText={emailErrorMessageKey}
          error={Boolean(emailErrorMessageKey)}
          required
          fullWidth
          {...register('emailAddress', {
            required: true,
            pattern: VALID_EMAIL_REGEX,
          })}
        />
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
            mt: spacingSizeMap.L,
          }}
        >
          <div />
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            disabled={isPending || isMergeFlowBusy}
          >
            {t('ui.event-management.learner.edit.save-details')}
          </Button>
        </Grid>
      </StyledForm>
      {mergeDialogProps && <MergeLearnersDialog {...mergeDialogProps} />}
      {mergeErrorDialogProps && (
        <MergeLearnersErrorDialog {...mergeErrorDialogProps} />
      )}
      <Snackbar
        open={isGeneralError}
        autoHideDuration={6000}
        onClose={resetMutation}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={resetMutation}
        >
          <AlertTitle>
            {t('ui.event-management.learner.edit.error.title')}
          </AlertTitle>
          {t('ui.event-management.learner.edit.error.details')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditLearnerForm;
